import React,{useState} from 'react'
import {motion} from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import {useAuth} from './AuthProvider'

const {FiCopy,FiSave,FiCheck,FiPlay,FiLoader,FiDollarSign,FiCpu,FiAlertCircle,FiShield}=FiIcons

const PromptPreview=({prompt,onCopy,onSave,showCopySuccess,promptData})=> {
  const {user}=useAuth()
  const [isRunning,setIsRunning]=useState(false)
  const [aiResponse,setAiResponse]=useState('')
  const [usage,setUsage]=useState(null)
  const [showAiResponse,setShowAiResponse]=useState(false)
  const [error,setError]=useState(null)
  const [useSecureApi,setUseSecureApi]=useState(true)

  const runPrompt=async ()=> {
    if (!prompt || isRunning) return
    
    setIsRunning(true)
    setAiResponse('')
    setUsage(null)
    setError(null)
    setShowAiResponse(true)
    
    try {
      // Choose API endpoint based on security preference
      const apiEndpoint=useSecureApi ? '/api/openai-secure' : '/api/openai'
      console.log(`Sending request to ${apiEndpoint}...`)
      
      const requestBody={
        prompt,
        features: promptData.features || [],
        complexity: promptData.complexity || 'intermediate',
        framework: promptData.framework || 'react',
        styling: promptData.styling || 'tailwind'
      }
      
      // Only add API key if using user-provided API
      if (!useSecureApi) {
        if (!user) {
          throw new Error('Please sign in to use user API features')
        }
        
        // Fetch user OpenAI key via our NCB proxy
        const res = await fetch(`/api/_ncb/query`, {
          method: 'POST',
          headers: { 'Content-Type':'application/json' },
          body: JSON.stringify({
            action: 'read',
            table: 'user_settings_a7k9m2x3p4',
            filters: [{ key:'user_id', value: user.id }],
            limit: 1
          })
        });
        if (!res.ok) throw new Error('Failed to load user settings');
        const j = await res.json();
        const row = Array.isArray(j?.data) ? j.data[0] : j?.data;
        const openaiApiKey = row?.openai_api_key;
        if (!openaiApiKey) throw new Error('Please add your OpenAI API key in Settings');
        
        requestBody.apiKey=openaiApiKey
      }
      
      const response=await fetch(apiEndpoint,{
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      })
      
      console.log('Response status:',response.status)
      
      // Handle non-streaming error responses
      if (!response.ok) {
        let errorMessage=`Request failed with status ${response.status}`
        
        // Check content type to determine how to handle the error
        const contentType=response.headers.get('content-type')
        if (contentType && contentType.includes('application/json')) {
          try {
            const errorData=await response.json()
            errorMessage=errorData.error || errorData.message || errorMessage
          } catch (jsonError) {
            console.warn('Failed to parse error JSON:',jsonError)
            errorMessage=`Server error: ${response.status} ${response.statusText}`
          }
        } else {
          // For non-JSON responses,just use status info
          errorMessage=`Server error: ${response.status} ${response.statusText}`
        }
        
        throw new Error(errorMessage)
      }
      
      // Check if response is streaming
      const contentType=response.headers.get('content-type')
      if (!contentType || !contentType.includes('text/event-stream')) {
        throw new Error('Expected streaming response but got: ' + (contentType || 'unknown content type'))
      }
      
      const reader=response.body.getReader()
      const decoder=new TextDecoder()
      
      while (true) {
        const {value,done}=await reader.read()
        if (done) break
        
        const chunk=decoder.decode(value,{stream: true})
        const lines=chunk.split('\n')
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data=JSON.parse(line.slice(6))
              if (data.content) {
                setAiResponse(prev=> prev + data.content)
              }
              if (data.done && data.usage) {
                setUsage(data.usage)
              }
              if (data.error) {
                console.error('Stream error:',data.error)
                setError(data.error)
              }
            } catch (parseError) {
              // Ignore parsing errors for incomplete JSON chunks
              console.debug('Ignoring JSON parse error for chunk:',line)
            }
          }
        }
      }
    } catch (error) {
      console.error('Error running prompt:',error)
      setError(error.message)
      setAiResponse('')
    } finally {
      setIsRunning(false)
    }
  }
  
  const copyAiResponse=async ()=> {
    if (aiResponse) {
      try {
        await navigator.clipboard.writeText(aiResponse)
      } catch (err) {
        console.error('Failed to copy AI response:',err)
      }
    }
  }
  
  if (!prompt) {
    return (
      <div className="bg-slate-50 rounded-xl p-4 sm:p-6 text-center">
        <p className="text-slate-500 text-sm sm:text-base">Generate a prompt to see the preview</p>
      </div>
    )
  }
  
  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Original Prompt Preview */}
      <div className="bg-slate-50 rounded-xl p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 sm:mb-4 space-y-2 sm:space-y-0">
          <h3 className="text-base sm:text-lg font-semibold text-slate-800">Generated Prompt</h3>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
            {/* API Security Toggle */}
            <div className="flex items-center space-x-2 bg-white px-2 sm:px-3 py-2 rounded-lg border border-slate-200">
              <SafeIcon icon={FiShield} className={`w-3 h-3 sm:w-4 sm:h-4 ${useSecureApi ? 'text-green-600' : 'text-orange-600'}`} />
              <label className="text-xs sm:text-sm font-medium text-slate-700 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={useSecureApi} 
                  onChange={(e)=> setUseSecureApi(e.target.checked)} 
                  className="sr-only" 
                />
                <span className={useSecureApi ? 'text-green-700' : 'text-orange-700'}>
                  {useSecureApi ? 'Secure API' : 'User API'}
                </span>
              </label>
            </div>
            
            <div className="flex space-x-2">
              <motion.button
                whileHover={{scale: 1.05}}
                whileTap={{scale: 0.95}}
                onClick={runPrompt}
                disabled={isRunning || (!useSecureApi && !user)}
                className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm ${
                  isRunning ? 'bg-orange-500 text-white cursor-not-allowed' : 'bg-green-500 text-white hover:bg-green-600'
                } ${(!useSecureApi && !user) ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <SafeIcon icon={isRunning ? FiLoader : FiPlay} className={`w-3 h-3 sm:w-4 sm:h-4 ${isRunning ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">{isRunning ? 'Running...' : 'Run with AI'}</span>
                <span className="sm:hidden">{isRunning ? 'Run...' : 'Run'}</span>
              </motion.button>
              
              <motion.button
                whileHover={{scale: 1.05}}
                whileTap={{scale: 0.95}}
                onClick={onCopy}
                className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm ${
                  showCopySuccess ? 'bg-green-500 text-white' : 'bg-red-500 text-white hover:bg-red-600'
                }`}
              >
                <SafeIcon icon={showCopySuccess ? FiCheck : FiCopy} className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">{showCopySuccess ? 'Copied!' : 'Copy'}</span>
              </motion.button>
              
              <motion.button
                whileHover={{scale: 1.05}}
                whileTap={{scale: 0.95}}
                onClick={onSave}
                className="px-3 sm:px-4 py-2 bg-slate-600 text-white rounded-lg font-medium hover:bg-slate-700 transition-colors flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm"
              >
                <SafeIcon icon={FiSave} className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Save</span>
              </motion.button>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-3 sm:p-4 border border-slate-200 max-h-64 sm:max-h-96 overflow-y-auto">
          <pre className="text-xs sm:text-sm text-slate-700 whitespace-pre-wrap font-mono">
            {prompt}
          </pre>
        </div>
        
        {/* API Security Info */}
        <div className="mt-3 sm:mt-4 p-2 sm:p-3 rounded-lg">
          {useSecureApi ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-2 sm:p-3">
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiShield} className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
                <p className="text-xs sm:text-sm text-green-700">
                  <strong>Secure Mode:</strong> Using system-managed API key stored securely in Supabase. No user account required.
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-2 sm:p-3">
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiShield} className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600" />
                <p className="text-xs sm:text-sm text-orange-700">
                  <strong>User Mode:</strong> Using your personal API key. Please sign in and add your OpenAI API key in Settings.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* AI Response Preview */}
      {showAiResponse && (
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          className="bg-slate-50 rounded-xl p-4 sm:p-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 sm:mb-4 space-y-2 sm:space-y-0">
            <div className="flex items-center space-x-2">
              <h3 className="text-base sm:text-lg font-semibold text-slate-800">AI Generated Code</h3>
              {isRunning && (
                <SafeIcon icon={FiLoader} className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 animate-spin" />
              )}
            </div>
            
            {aiResponse && (
              <motion.button
                whileHover={{scale: 1.05}}
                whileTap={{scale: 0.95}}
                onClick={copyAiResponse}
                className="px-3 sm:px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm"
              >
                <SafeIcon icon={FiCopy} className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>Copy Code</span>
              </motion.button>
            )}
          </div>
          
          <div className="bg-white rounded-lg p-3 sm:p-4 border border-slate-200 max-h-64 sm:max-h-96 overflow-y-auto">
            {error ? (
              <div className="flex items-center space-x-2 text-red-600">
                <SafeIcon icon={FiAlertCircle} className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-xs sm:text-sm">Error: {error}</span>
              </div>
            ) : aiResponse ? (
              <pre className="text-xs sm:text-sm text-slate-700 whitespace-pre-wrap font-mono">
                {aiResponse}
              </pre>
            ) : (
              <div className="flex items-center justify-center py-6 sm:py-8">
                <div className="text-center">
                  <SafeIcon icon={FiLoader} className="w-6 h-6 sm:w-8 sm:h-8 text-red-500 animate-spin mx-auto mb-2" />
                  <p className="text-slate-500 text-sm">Generating code...</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Usage Statistics */}
          {usage && (
            <motion.div
              initial={{opacity: 0,y: 10}}
              animate={{opacity: 1,y: 0}}
              className="mt-3 sm:mt-4 p-2 sm:p-3 bg-green-50 rounded-lg"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-2 sm:space-y-0">
                <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-4">
                  <div className="flex items-center space-x-1">
                    <SafeIcon icon={FiCpu} className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
                    <span className="text-xs sm:text-sm font-medium text-green-700">
                      {usage.total_tokens.toLocaleString()} tokens
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <SafeIcon icon={FiDollarSign} className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
                    <span className="text-xs sm:text-sm font-medium text-green-700">
                      ~${usage.estimated_cost.toFixed(4)}
                    </span>
                  </div>
                </div>
                <span className="text-xs text-green-600">📊 Generation Complete</span>
              </div>
            </motion.div>
          )}
        </motion.div>
      )}
    </div>
  )
}

export default PromptPreview